import { State } from '@progress/kendo-data-query';
import { CancelTokenSource } from 'axios';
import { isEqual } from 'lodash';
import {
  Dispatch,
  FC,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import usePostMessage from 'src/hooks/usePostMessage';
import useAttachDocumentInvoicesApi from 'src/services/useAttachDocumentInvoiceApi';
import { CompanyInvoiceScans } from 'src/services/useCompanyInvoiceScansApi';
import useCreateOcrDocumentApi, {
  CreateOcrDocumentParams,
  CreateOcrDocumentResponse,
} from 'src/services/useCreateOcrDocumentApi';
import useGetOcrFailedMessageApi from 'src/services/useGetOcrFailedMessageApi';
import useGetOcrRecordCountApi from 'src/services/useGetOcrRecordCountApi';
import useGetOnlineAndOfflineSuppliers, {
  IAllSuppliers,
} from 'src/services/useGetOnlineAndOfflineSuppliers';
import useMergeAttachmentApi, { MergeAttachmentRequest } from 'src/services/useMergeAttachmentApi';
import useReviewDocumentApi, {
  ReviewDocumentRequest,
  ReviewDocumentResponse,
} from 'src/services/useReviewDocumentApi';
import useReviewPoApi, {
  PoMatchStatus,
  ReviewPoRequest,
  ReviewPoResponse,
} from 'src/services/useReviewPoApi';
import { IAttachDocumentInvoices } from 'src/types/AttachDocumentInvoices';
import { DropDownCategories, SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { createContext } from 'use-context-selector';
import {
  useInvoiceScanManagerCategories,
  useOcrScanCategories,
} from './InvoiceScanManagerCategories';
import { invoiceOcrQueueFilter, invoiceScanManagerFilter } from './InvoiceScanManagerFilter';
import useReviewDocumentPanelValidation, {
  ValidationMessages,
} from './ocr/errors/useReviewDocumentPanelValidation';

interface InvoiceScanManagerContextType {
  count: number;
  showPrev: boolean;
  refreshCompany?: boolean;
  setShowPrev: SetState<boolean>;
  setRefreshCompany?: SetState<boolean>;
  refreshPage: VoidFunction;
  attachmentListData: IAttachDocumentInvoices[];
  selectedDocuments: IAttachDocumentInvoices[];
  index: number;
  setAttachmentListData: SetState<IAttachDocumentInvoices[]>;
  setIsDecrement: SetState<boolean>;
  previewDocument?: IAttachDocumentInvoices;
  setDataState: SetState<State>;
  dataState: State;
  isAttachDocumentLoading: boolean;
  handleCellClick: (attachId: number) => void;
  dropdownFilter: DropDownCategories;
  setDropdownFilter: SetState<DropDownCategories>;
  ocrCompanies: CompanyInvoiceScans;
  setOcrCompanies: SetState<CompanyInvoiceScans>;
  isOcrCompany: boolean;
  reviewModal: boolean;
  openReviewModal: () => void;
  closeReviewModal: () => void;
  selectedView: number;
  setSelectedView: SetState<number>;
  ocrAttachmentList: IAttachDocumentInvoices[];
  setOcrAttachmentList: SetState<IAttachDocumentInvoices[]>;
  ocrDataState: State;
  setOcrDataState: SetState<State>;
  ocrDropdownFilter: DropDownCategories;
  setOcrDropdownFilter: SetState<DropDownCategories>;
  isHidePreview: boolean;
  invoiceScanManagerFilterInitialState: DropDownCategories;
  ocrFilterInitialState: DropDownCategories;
  ocrRecordCount: number;
  getOcrRecordCount: (param?: undefined) => Promise<number>;
  docNumber: IOcrResult;
  setDocNumber: Dispatch<SetStateAction<IOcrResult>>;
  poNumber: IOcrResult;
  setPoNumber: Dispatch<SetStateAction<IOcrResult>>;
  selectedSupplier: IAllSuppliers;
  setSelectedSupplier: Dispatch<SetStateAction<IAllSuppliers>>;
  openOcrReviewModal: (attachId: number) => Promise<void>;
  supplierName: IOcrResult;
  isPoMatchModalOpen: boolean;
  openPoMatchModal: () => void;
  closePoMatchModal: () => void;
  getDocValidations: (attachId: number, docNumber?: string) => Promise<ReviewDocumentResponse>;
  reviewPoValidations: ReviewPoResponse;
  setReviewPoValidations: Dispatch<SetStateAction<ReviewPoResponse>>;
  getPoValidations: (attachId: number, poNum?: string, poId?: number) => Promise<ReviewPoResponse>;
  isReviewPanelLoading: boolean;
  reviewPoReqRef: MutableRefObject<CancelTokenSource | undefined>;
  reviewDocReqRef: MutableRefObject<CancelTokenSource | undefined>;
  reviewDocumentValidations: ReviewDocumentResponse;
  setReviewDocumentValidations: Dispatch<SetStateAction<ReviewDocumentResponse>>;
  ocrFailedValue: string;
  isReviewPoLoading: boolean;
  isReviewDocumentLoading: boolean;
  initialLoading: boolean;
  initialSelectedSupplier: IAllSuppliers;
  setInitialSelectedSupplier: Dispatch<SetStateAction<IAllSuppliers>>;
  onlineOfflineSuppliers: IAllSuppliers[];
  getAllOnlineOfflineSuppliers: (attachId: number, poMatchStatus: PoMatchStatus) => Promise<void>;
  createOcrDocument: (
    params?: CreateOcrDocumentParams | undefined
  ) => Promise<CreateOcrDocumentResponse>;
  isCreateAndConfirmDocumentLoading: boolean;
  ocrPreviewDocument: IAttachDocumentInvoices | undefined;
  validationMessages: ValidationMessages;
  isReviewPanelDirty: boolean;
  mergeAttachment: (params?: MergeAttachmentRequest | undefined) => Promise<any>;
  isMergeAttachmentLoading: boolean;
  isOcrQueueAutoReloading: boolean;
  disableRefresh: () => void;
  enableRefresh: () => void;
  initialReviewPoValidation: ReviewPoResponse;
  initialReviewDocValidation: ReviewDocumentResponse;
  latestClickedId: number;
  setLatestClickedId: SetState<number>;
  showDelete: boolean;
  setShowDelete: SetState<boolean>;
  showUploadsOlderThan90Days: boolean;
  setShowUploadsOlderThan90Days: SetState<boolean>;
  tab: string;
}

export interface IOcrResult {
  value: string;
  matchConfidence: string;
}

export interface IOriginalOcr {
  originalOcrPoNumber: string;
  originalOcrDocNumber: string;
  originalSelectedSupplier: string;
}

export const InvoiceScanManagerContext = createContext({} as InvoiceScanManagerContextType);

interface InvoiceScanManagerContextProviderProps {
  children: ReactNode;
}

const dateAttribute = ['creationDate', 'scannedOn'];

export const initialOcrDataState: State = {
  skip: 0,
  take: 100,
  sort: [
    {
      field: 'creationDate',
      dir: 'desc',
    },
  ],
};

export const defaultSelectedSupplier: IAllSuppliers = {
  supplierName: '',
  supplierId: 0,
  isOnlineSupplier: false,
  hasOcrDocument: false,
};

const InvoiceScanManagerContextProvider: FC<InvoiceScanManagerContextProviderProps> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const tab = useMemo(() => searchParams.get('tab')?.toLocaleLowerCase() || '', [searchParams]);
  const [showUploadsOlderThan90Days, setShowUploadsOlderThan90Days] = useState(
    tab === 'attachment' || tab === 'ocr'
  );
  const invoiceScanManagerFilterInitialState = useInvoiceScanManagerCategories()[0];
  const initialSelectedView = useMemo(() => (tab === 'ocr' ? 1 : 0), [tab]);
  const ocrFilterInitialState = useOcrScanCategories()[0];
  const {
    reviewPoMethod,
    reviewPoValidations,
    reqRef: reviewPoReqRef,
    isReviewPoLoading,
    setReviewPoValidations,
    initialReviewPoValidation,
    setInitialReviewPoValidation,
    reviewPoError,
  } = useReviewPoApi();
  const {
    reviewDocument,
    reviewDocumentValidations,
    isReviewDocumentLoading,
    reqRef: reviewDocReqRef,
    setReviewDocumentValidations,
    initialReviewDocValidation,
    setInitialReviewDocValidation,
    reviewDocumentError,
  } = useReviewDocumentApi();
  const [initialSelectedSupplier, setInitialSelectedSupplier] =
    useState<IAllSuppliers>(defaultSelectedSupplier);
  const [poNumber, setPoNumber] = useState<IOcrResult>({ value: '', matchConfidence: '' });
  const [count, setCount] = useState<number>(0);
  const { company: selectedCompany, changeCompany: setSelectedCompany } = useCompany();
  const { companies, user } = useAuth();
  const [refreshCompany, setRefreshCompany] = useState<boolean>(false);
  const [showPrev, setShowPrev] = useState<boolean>(true);
  const { attachDocument, isAttachDocumentLoading } = useAttachDocumentInvoicesApi();
  const { getOcrFailedMassage, ocrFailedValue } = useGetOcrFailedMessageApi();
  const { getAllSuppliers, onlineOfflineSuppliers, isAllSuppliersLoading } =
    useGetOnlineAndOfflineSuppliers();
  const { getOcrRecordCount, ocrRecordCount } = useGetOcrRecordCountApi(
    selectedCompany?.companyId,
    showUploadsOlderThan90Days
  );
  const { createOcrDocument, isCreateAndConfirmDocumentLoading } = useCreateOcrDocumentApi();
  const { mergeAttachment, isMergeAttachmentLoading } = useMergeAttachmentApi();
  const [attachmentListData, setAttachmentListData] = useState<IAttachDocumentInvoices[]>([]);
  const [isDecrement, setIsDecrement] = useState<boolean>(false);
  const [dropdownFilter, setDropdownFilter] = useState<DropDownCategories>(
    useInvoiceScanManagerCategories()[tab === 'attachment' ? 2 : 0]
  );

  const [selectedView, setSelectedView] = useState<number>(initialSelectedView);
  const { value: reviewModal, setTrue: openReviewModal, setFalse: closeModal } = useBoolean();
  const {
    value: isRefreshEnabled,
    setTrue: enableRefresh,
    setFalse: disableRefresh,
  } = useBoolean(true);
  const [ocrCompanies, setOcrCompanies] = useState<CompanyInvoiceScans>([] as CompanyInvoiceScans);
  const [ocrAttachmentList, setOcrAttachmentList] = useState<IAttachDocumentInvoices[]>([]);
  const [ocrDropdownFilter, setOcrDropdownFilter] = useState(
    useOcrScanCategories()[tab === 'ocr' ? 2 : 0]
  );

  const [docNumber, setDocNumber] = useState<IOcrResult>({ value: '', matchConfidence: '' });
  const [selectedSupplier, setSelectedSupplier] = useState<IAllSuppliers>(defaultSelectedSupplier);
  const [supplierName, setSupplierName] = useState<IOcrResult>({ value: '', matchConfidence: '' });
  const [ocrPreviewDocument, setOcrPreviewDocument] = useState<IAttachDocumentInvoices | undefined>(
    undefined
  );
  const [latestClickedId, setLatestClickedId] = useState(0);
  const [showDelete, setShowDelete] = useState(false);

  const {
    value: initialLoading,
    setFalse: stopInitialLoading,
    setTrue: startInitialLoading,
  } = useBoolean();
  const {
    value: isPoMatchModalOpen,
    setTrue: openPoMatchModal,
    setFalse: closePoMatchModal,
  } = useBoolean();
  const {
    value: isOcrQueueAutoReloading,
    setTrue: startOcrQueueAutoReloading,
    setFalse: stopOcrQueueAutoReloading,
  } = useBoolean();

  const isReviewPanelLoading = useMemo(
    () => isReviewPoLoading || isReviewDocumentLoading || isAllSuppliersLoading,
    [isAllSuppliersLoading, isReviewDocumentLoading, isReviewPoLoading]
  );

  const validationMessages = useReviewDocumentPanelValidation(
    docNumber,
    poNumber,
    selectedSupplier,
    initialReviewDocValidation,
    reviewDocumentValidations,
    reviewPoValidations,
    initialLoading,
    isReviewDocumentLoading,
    isReviewPoLoading,
    isReviewPanelLoading,
    reviewPoError,
    reviewDocumentError
  );

  const isReviewPanelDirty = useMemo(() => {
    const initialReviewPanelData = {
      poNumber: initialReviewPoValidation?.ocrPoNumber ?? '',
      docNumber: initialReviewDocValidation?.ocrDocNumber ?? '',
      supplierName: initialSelectedSupplier.supplierName ?? '',
    };
    const currentReviewPanelData = {
      poNumber: poNumber.value ?? '',
      docNumber: docNumber.value ?? '',
      supplierName: selectedSupplier.supplierName ?? '',
    };

    return !isEqual(initialReviewPanelData, currentReviewPanelData);
  }, [
    docNumber.value,
    initialReviewDocValidation.ocrDocNumber,
    initialReviewPoValidation.ocrPoNumber,
    initialSelectedSupplier.supplierName,
    poNumber.value,
    selectedSupplier.supplierName,
  ]);

  const isOcrCompany = useMemo(
    () =>
      !!ocrCompanies.find((company) => company.companyId === Number(selectedCompany.companyId))
        ?.usesOcr,
    [ocrCompanies, selectedCompany?.companyId]
  );

  const [dataState, setDataState] = useState(initialOcrDataState);
  const [ocrDataState, setOcrDataState] = useState(initialOcrDataState);

  const index = useMemo(
    () =>
      selectedView === 0
        ? attachmentListData.findIndex((element) => element.preview)
        : ocrAttachmentList.findIndex((element) => element.preview),
    [attachmentListData, ocrAttachmentList, selectedView]
  );

  const selectedDocuments = useMemo(
    () =>
      selectedView === 0
        ? attachmentListData.filter((element) => element.selected)
        : ocrAttachmentList.filter((element) => element.selected),
    [attachmentListData, ocrAttachmentList, selectedView]
  );

  const previewDocument = useMemo(
    () =>
      selectedView === 0
        ? attachmentListData.find((element) => element.preview)
        : ocrAttachmentList.find((element) => element.preview),
    [attachmentListData, ocrAttachmentList, selectedView]
  );

  const isHidePreview = useMemo(
    () => selectedView === 1 && !isOcrCompany,
    [isOcrCompany, selectedView]
  );

  const getPoValidations = useCallback(
    async (attachId: number, poNum?: string, poId?: number) => {
      const baseRequest: ReviewPoRequest = {
        attachmentId: attachId,
        companyId: selectedCompany.companyId,
      };
      if (poNum) {
        baseRequest.poNumber = poNum;
      }
      if (poId) {
        baseRequest.poId = poId;
      }
      const response: ReviewPoResponse = await reviewPoMethod(baseRequest);
      return response;
    },
    [reviewPoMethod, selectedCompany.companyId]
  );

  const getDocValidations = useCallback(
    async (attachId: number, docNumber?: string) => {
      const baseDocRequest: ReviewDocumentRequest = {
        attachmentId: attachId,
        companyId: selectedCompany.companyId,
      };
      if (docNumber) {
        baseDocRequest.invoiceNumber = docNumber;
      }
      const response: ReviewDocumentResponse = await reviewDocument(baseDocRequest);
      return response;
    },
    [selectedCompany.companyId, reviewDocument]
  );

  const getAttachDocuments = useCallback(() => {
    setAttachmentListData([]);
    const requestBody = modifyRequest(dataState, dateAttribute, undefined, ['invoiceNumber']);
    requestBody.filters = [
      ...requestBody.filters,
      ...(invoiceScanManagerFilter[dropdownFilter.value]?.filters || []),
      ...(!showDelete ? [{ field: 'isDeleted', value: 'false', operator: 'eq' }] : []),
    ];
    requestBody.sort = [
      ...requestBody.sort,
      ...(invoiceScanManagerFilter[dropdownFilter.value]?.sort || []),
    ];
    const sourceFiledIndex = requestBody.filters.findIndex((filter) => filter.field === 'source');
    if (sourceFiledIndex !== -1) {
      const element = requestBody.filters[sourceFiledIndex];
      const { value } = element;
      const isSourceIncludeOcr = value.toString().endsWith('(OCR)');
      if (isSourceIncludeOcr) {
        requestBody.filters[sourceFiledIndex].value = value.toString().split('(')[0].trim();
        requestBody.filters.push({ field: 'ocrStatus', value: 'Success', operator: 'contains' });
      } else {
        requestBody.filters.push({
          field: 'ocrStatus',
          value: 'Success',
          operator: 'doesnotcontain',
        });
      }
    }

    if (requestBody && selectedCompany?.companyId) {
      attachDocument({
        companyId: selectedCompany.companyId,
        body: requestBody,
        uploadsOlderThan90Days: showUploadsOlderThan90Days,
      }).then((res) => {
        try {
          if (res.totalRecords > 0) {
            if (isDecrement) {
              res.items[res.items.length - 1].preview = true;
            } else {
              res.items[0].preview = true;
            }
          }
          setCount(res.totalRecords);
          setAttachmentListData(res?.items);
        } catch (error) {
          console.error('error', error);
        }
      });
    }
  }, [
    attachDocument,
    dataState,
    dropdownFilter.value,
    isDecrement,
    selectedCompany.companyId,
    showDelete,
    showUploadsOlderThan90Days,
  ]);

  const getOcrAttachDocuments = useCallback(
    (autoRefresh: boolean = false) => {
      if (autoRefresh) {
        startOcrQueueAutoReloading();
      } else {
        setOcrAttachmentList([]);
      }
      const requestBody = modifyRequest(ocrDataState, dateAttribute, undefined, ['invoiceNumber']);
      requestBody.filters = [
        ...requestBody.filters,
        ...(invoiceOcrQueueFilter[ocrDropdownFilter.value]?.filters || []),
      ];
      requestBody.sort = [
        ...requestBody.sort,
        ...(invoiceOcrQueueFilter[ocrDropdownFilter.value]?.sort || []),
      ];

      if (requestBody && selectedCompany?.companyId) {
        attachDocument({
          companyId: selectedCompany.companyId,
          body: requestBody,
          uploadsOlderThan90Days: showUploadsOlderThan90Days,
        })
          .then((res) => {
            try {
              if (res.totalRecords > 0) {
                if (isDecrement) {
                  res.items[res.items.length - 1].preview = true;
                } else {
                  res.items[0].preview = true;
                }
              }
              setCount(res.totalRecords);
              setOcrAttachmentList(res?.items);
            } catch (error) {
              console.error(error);
            }
          })
          .finally(stopOcrQueueAutoReloading);
      }
    },
    [
      attachDocument,
      isDecrement,
      ocrDataState,
      ocrDropdownFilter.value,
      selectedCompany.companyId,
      startOcrQueueAutoReloading,
      stopOcrQueueAutoReloading,
      showUploadsOlderThan90Days,
    ]
  );

  const handleCellClick = useCallback(
    (attachId: number) => {
      if (selectedView === 0) {
        setAttachmentListData((prev) =>
          [...prev].map((listItem) => ({
            ...listItem,
            preview: attachId === listItem.attachId,
          }))
        );
      } else {
        setOcrAttachmentList((prev) =>
          [...prev].map((listItem) => ({
            ...listItem,
            preview: attachId === listItem.attachId,
          }))
        );
      }
    },
    [selectedView]
  );

  const refreshPage = useCallback(() => {
    if (selectedView === 1 && isOcrCompany) {
      getOcrAttachDocuments();
    } else if (selectedView === 0) {
      getAttachDocuments();
    }
  }, [getAttachDocuments, getOcrAttachDocuments, isOcrCompany, selectedView]);

  const getAllOnlineOfflineSuppliers = useCallback(
    async (attachId: number, poStatus: PoMatchStatus) => {
      if (
        !(
          poStatus === 'ConfirmedMatchCreatedByCurrentUser' ||
          poStatus === 'ConfirmedMatchCreatedByOtherUser'
        )
      ) {
        await getAllSuppliers(attachId);
      }
    },
    [getAllSuppliers]
  );

  const openOcrReviewModal = useCallback(
    async (attachId: number) => {
      try {
        disableRefresh();
        openReviewModal();
        startInitialLoading();
        setOcrPreviewDocument(ocrAttachmentList.find((doc) => doc.attachId === attachId));
        const result = await Promise.all([getPoValidations(attachId), getDocValidations(attachId)]);
        setInitialReviewPoValidation(result[0]);
        setInitialReviewDocValidation(result[1]);
        setPoNumber({
          value: result[0]?.ocrPoNumber || '',
          matchConfidence: result[0]?.matchConfidence || '',
        });
        setDocNumber({
          value: result[1].ocrDocNumber || '',
          matchConfidence: result[1].matchConfidence || '',
        });
        setSupplierName({
          value: result[0]?.supplierMatchReviewResult?.ocrSupplierName || '',
          matchConfidence: result[0]?.supplierMatchReviewResult?.matchConfidence || '',
        });
        stopInitialLoading();
      } catch (error) {
        stopInitialLoading();
      }
    },
    [
      disableRefresh,
      getDocValidations,
      getPoValidations,
      ocrAttachmentList,
      openReviewModal,
      setInitialReviewDocValidation,
      setInitialReviewPoValidation,
      startInitialLoading,
      stopInitialLoading,
    ]
  );

  useEffect(() => {
    if (reviewModal && reviewPoValidations.matchStatus && onlineOfflineSuppliers.length === 0) {
      getAllOnlineOfflineSuppliers(
        Number(ocrPreviewDocument?.attachId),
        reviewPoValidations.matchStatus
      );
    }
  }, [
    getAllOnlineOfflineSuppliers,
    getAttachDocuments,
    getOcrAttachDocuments,
    isOcrCompany,
    ocrPreviewDocument?.attachId,
    onlineOfflineSuppliers.length,
    reviewModal,
    reviewPoValidations.matchStatus,
    selectedView,
  ]);

  const closeReviewModal = useCallback(() => {
    closeModal();
    if (reviewPoReqRef.current) {
      reviewPoReqRef.current.cancel();
    }
    if (reviewDocReqRef.current) {
      reviewDocReqRef.current.cancel();
    }
    setInitialReviewPoValidation({} as ReviewPoResponse);
    setInitialReviewDocValidation({} as ReviewDocumentResponse);
    setPoNumber({ value: '', matchConfidence: '' });
    setSupplierName({ value: '', matchConfidence: '' });
    setDocNumber({ value: '', matchConfidence: '' });
    setSelectedSupplier({} as IAllSuppliers);
    enableRefresh();
  }, [
    closeModal,
    enableRefresh,
    reviewDocReqRef,
    reviewPoReqRef,
    setInitialReviewDocValidation,
    setInitialReviewPoValidation,
  ]);

  useEffect(() => {
    if ((tab === 'attachment' || tab === 'ocr') && ocrCompanies.length > 0) {
      const company = companies.find((company) => company.companyId === user.CompanyId);
      if (company) {
        setSelectedCompany(company);
      }
    }
  }, [companies, ocrCompanies.length, setSelectedCompany, tab, user.CompanyId]);

  useEffect(() => {
    setDataState(initialOcrDataState);
    setOcrDataState(initialOcrDataState);
  }, [isOcrCompany, selectedCompany.companyId]);

  useEffect(() => {
    if (previewDocument?.attachId && previewDocument?.ocrStatus === 'Failed') {
      getOcrFailedMassage(previewDocument?.attachId);
    }
  }, [getOcrFailedMassage, previewDocument]);

  useEffect(() => {
    if (selectedCompany.companyId && isOcrCompany) {
      getOcrRecordCount();
    }
  }, [getOcrRecordCount, isOcrCompany, selectedCompany.companyId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      refreshPage();
    }, 100);
    return () => clearTimeout(timer);
  }, [refreshPage]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (selectedCompany.companyId && selectedView === 1 && isRefreshEnabled) {
        getOcrAttachDocuments(true);
        getOcrRecordCount();
        setRefreshCompany?.((prev) => !prev);
      }
    }, 60000);
    return () => clearInterval(intervalId);
  }, [
    getOcrAttachDocuments,
    getOcrRecordCount,
    isRefreshEnabled,
    refreshPage,
    reviewModal,
    selectedCompany,
    selectedView,
  ]);

  const dirtyMessage = useMemo(() => ({ isDirty: isReviewPanelDirty }), [isReviewPanelDirty]);
  usePostMessage(dirtyMessage);

  return (
    <InvoiceScanManagerContext.Provider
      value={{
        count,
        showPrev,
        setShowPrev,
        attachmentListData,
        refreshPage,
        refreshCompany,
        setRefreshCompany,
        selectedDocuments,
        setAttachmentListData,
        index,
        previewDocument,
        setDataState,
        dataState,
        isAttachDocumentLoading,
        setIsDecrement,
        handleCellClick,
        dropdownFilter,
        setDropdownFilter,
        ocrCompanies,
        setOcrCompanies,
        isOcrCompany,
        reviewModal,
        openReviewModal,
        closeReviewModal,
        selectedView,
        setSelectedView,
        ocrAttachmentList,
        setOcrAttachmentList,
        ocrDataState,
        setOcrDataState,
        ocrDropdownFilter,
        setOcrDropdownFilter,
        isHidePreview,
        invoiceScanManagerFilterInitialState,
        ocrFilterInitialState,
        ocrRecordCount,
        getOcrRecordCount,
        poNumber,
        setPoNumber,
        docNumber,
        setDocNumber,
        selectedSupplier,
        setSelectedSupplier,
        openOcrReviewModal,
        closePoMatchModal,
        openPoMatchModal,
        isPoMatchModalOpen,
        getDocValidations,
        ocrFailedValue,
        reviewPoValidations,
        setReviewPoValidations,
        getPoValidations,
        isReviewPanelLoading,
        supplierName,
        reviewPoReqRef,
        reviewDocReqRef,
        reviewDocumentValidations,
        setReviewDocumentValidations,
        isReviewPoLoading,
        isReviewDocumentLoading,
        initialLoading,
        initialSelectedSupplier,
        setInitialSelectedSupplier,
        onlineOfflineSuppliers,
        getAllOnlineOfflineSuppliers,
        createOcrDocument,
        isCreateAndConfirmDocumentLoading,
        validationMessages,
        isReviewPanelDirty,
        mergeAttachment,
        isMergeAttachmentLoading,
        ocrPreviewDocument,
        isOcrQueueAutoReloading,
        disableRefresh,
        enableRefresh,
        initialReviewPoValidation,
        initialReviewDocValidation,
        latestClickedId,
        setLatestClickedId,
        showDelete,
        setShowDelete,
        showUploadsOlderThan90Days,
        setShowUploadsOlderThan90Days,
        tab,
      }}
    >
      {children}
    </InvoiceScanManagerContext.Provider>
  );
};

export default InvoiceScanManagerContextProvider;
